import { useEffect } from 'react'

import { messagesApi } from '@/api/modules/messages'
import { useAuth } from '@/hooks/useAuth'

/** VAPID key bytes for `PushManager.subscribe` (must be `BufferSource`, not `Uint8Array<ArrayBufferLike>`). */
function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const out = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    out[i] = rawData.charCodeAt(i)
  }
  return out
}

/**
 * 注册 Service Worker（由 vite-plugin-pwa 生成，已通过 importScripts
 * 合并了 /push-sw.js 中的 push/notificationclick 事件处理），并向浏览器
 * 订阅 Web Push，再把订阅信息上报到后端。
 *
 * 组件不渲染任何内容，仅在登录状态变化时运行一次副作用；如果用户拒绝授权
 * 或浏览器不支持 Push API，会静默失败（推送属于可选功能）。
 */
export function PushManager(): null {
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }
    if (typeof window === 'undefined') {
      return
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return
    }

    let cancelled = false

    ;(async () => {
      try {
        // 等待 vite-plugin-pwa 注册的 SW 进入 active 状态
        const reg = await navigator.serviceWorker.ready

        const keyRes = await messagesApi.getVapidKey()
        if (!keyRes.success || !keyRes.data?.publicKey) {
          return
        }

        let sub = await reg.pushManager.getSubscription()
        if (!sub) {
          const permission = await Notification.requestPermission()
          if (permission !== 'granted' || cancelled) {
            return
          }

          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey),
          })
        }

        if (!cancelled) {
          await messagesApi.subscribePush(sub.toJSON())
        }
      } catch {
        // 推送是可选能力；静默失败不影响主流程
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  return null
}

export default PushManager
